import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import {
  ConfigOptions,
  ConfigService,
  PropertyAvailability,
  PROPERTY_AVAILABILITY_LEVELS,
} from '@wilson/config';
import { Observable, map, mergeMap } from 'rxjs';
import { ActivityModel } from '@wilson/activities/models';
import { NestedKeyOf } from '@wilson/utils';

/**
 * Type-safe mapping from backend property names to ActivityModel field paths.
 *
 * Keys are the property names as returned by the API.
 * Values are validated at compile-time to be valid paths in ActivityModel.
 *
 * @see ActivityModel for the actual data model
 */
const PROPERTY_TO_MODEL_FIELD = {
  // Core fields
  activityCategory: 'activityCategoryId',
  name: 'name',

  // Time and location
  startLocation: 'plannedRoute.startLocationId',
  endLocation: 'plannedRoute.endLocationId',
  startDatetime: 'plannedTimes.start',
  endDatetime: 'plannedTimes.end',
  reportedStartLocation: 'reportedRoute.startLocationId',
  reportedEndLocation: 'reportedRoute.endLocationId',
  reportedStartDatetime: 'reportedTimes.start',
  reportedEndDatetime: 'reportedTimes.end',

  // Shift validation
  client: 'clientId',
  agreement: 'agreementId',

  // Transport-specific
  emissionClass: 'vehicleEmissionClassId',
  travelDirection: 'travelDirection',
  organizationalUnitTransportGoods: 'organizationalUnitTransportGoodsId',
  numberOfWagons: 'numberOfWagons',
  tonnage: 'tonnage',

  // Additional information
  cargo: 'cargo',
  track: 'track',
  externalNumber: 'externalNumber',
  orderNumber: 'orderNumber',
  trainNumber: 'trainNumber',
  wagonNumber: 'wagonNumber',
  locomotiveNumber: 'locomotiveNumber',

  // Route knowledge validation
  validateRoute: 'validateRoute',

  // Some properties are intentionally not included in the mapping because
  // they are not yet implemented in the ActivityModel or are not planned to
  // be configurable in the portal in the portal settings.
} as const satisfies Record<string, NestedKeyOf<ActivityModel>>;

/**
 * Frontend-only properties that are not yet supported by the backend API.
 * These will be automatically injected with default values in getSettings()
 * and filtered out in updateSettings() to avoid API errors.
 */
const FRONTEND_ONLY_PROPERTIES: ReadonlyArray<{
  propertyName: NestedKeyOf<ActivityModel>;
  portalAvailability: PropertyAvailability;
  mobileAvailability: PropertyAvailability;
}> = [
  {
    propertyName: 'professionId',
    portalAvailability: 'editable',
    mobileAvailability: 'notAvailable',
  },
  {
    propertyName: 'qualificationIds',
    portalAvailability: 'editable',
    mobileAvailability: 'notAvailable',
  },
  {
    propertyName: 'qualificationProfileIds',
    portalAvailability: 'editable',
    mobileAvailability: 'notAvailable',
  },
  {
    propertyName: 'validateRoute',
    portalAvailability: 'editable',
    mobileAvailability: 'notAvailable',
  },
] as const;

/**
 * Mapping of grouped properties to their nested properties.
 * Used for merging/splitting settings between the API (nested) and UI (grouped).
 */
const GROUPED_TO_NESTED_PROPERTIES: Record<
  string,
  readonly NestedKeyOf<ActivityModel>[]
> = {
  plannedTimes: ['plannedTimes.start', 'plannedTimes.end'],
  reportedTimes: ['reportedTimes.start', 'reportedTimes.end'],
  plannedRoute: ['plannedRoute.startLocationId', 'plannedRoute.endLocationId'],
  reportedRoute: [
    'reportedRoute.startLocationId',
    'reportedRoute.endLocationId',
  ],
} as const;

/**
 * Merges nested property settings into a grouped property setting.
 * Takes the most restrictive (minimum level) availability from all nested properties.
 *
 * @remarks
 * This is required because the API returns settings for the individual properties
 * (e.g. startLocation, reportedEndDatetime), but the UI needs to display them as a
 * single grouped setting (plannedTimes) for better UX. The merging logic ensures
 * that if any nested property is not available, the grouped property is also not
 * available, and if any nested property is read-only, the grouped property is at
 * most read-only.
 *
 * @param nestedSettings - Array of nested settings to merge
 * @param groupedPropertyName - Name of the grouped property
 * @returns Merged grouped setting, or null if no nested settings found
 */
function mergeNestedSettings(
  nestedSettings: ActivityPropertySetting[],
  groupedPropertyName: string,
): ActivityPropertySetting | null {
  if (nestedSettings.length === 0) return null;

  // Find most restrictive (minimum level) for portal
  const portalAvailability = nestedSettings.reduce((min, setting) => {
    const currentLevel =
      PROPERTY_AVAILABILITY_LEVELS[setting.portalAvailability];
    const minLevel = PROPERTY_AVAILABILITY_LEVELS[min];
    return currentLevel < minLevel ? setting.portalAvailability : min;
  }, 'editable' as PropertyAvailability);

  // Find most restrictive (minimum level) for mobile
  const mobileAvailability = nestedSettings.reduce((min, setting) => {
    const currentLevel =
      PROPERTY_AVAILABILITY_LEVELS[setting.mobileAvailability];
    const minLevel = PROPERTY_AVAILABILITY_LEVELS[min];
    return currentLevel < minLevel ? setting.mobileAvailability : min;
  }, 'editable' as PropertyAvailability);

  return {
    id: `merged-${groupedPropertyName}`,
    propertyName: groupedPropertyName as NestedKeyOf<ActivityModel>,
    portalAvailability,
    mobileAvailability,
  };
}

/**
 * Splits a grouped property setting into nested property settings.
 * Applies the grouped property's availability to all nested properties.
 *
 * @param groupedSetting - Grouped setting to split
 * @param nestedPropertyNames - Names of nested properties
 * @param originalSettings - Original settings array to preserve IDs
 * @returns Array of nested settings
 */
function splitGroupedSetting(
  groupedSetting: ActivityPropertySetting,
  nestedPropertyNames: readonly NestedKeyOf<ActivityModel>[],
  originalSettings: ActivityPropertySetting[],
): ActivityPropertySetting[] {
  return nestedPropertyNames.map((nestedProp) => {
    // Find original setting to preserve its ID
    const original = originalSettings.find(
      (s) => s.propertyName === nestedProp,
    );
    return {
      id: original?.id ?? `split-${nestedProp}`,
      propertyName: nestedProp,
      portalAvailability: groupedSetting.portalAvailability,
      mobileAvailability: groupedSetting.mobileAvailability,
    };
  });
}

/**
 * Reverse mapping from ActivityModel field paths to back-end property names.
 * Used when sending updates back to the API.
 */
const MODEL_FIELD_TO_PROPERTY = Object.fromEntries(
  Object.entries(PROPERTY_TO_MODEL_FIELD).map(([k, v]) => [v, k]),
) as Record<NestedKeyOf<ActivityModel>, ActivityPropertyName>;

/**
 * Back-end property names that are implemented in the ActivityModel.
 * Extracted from the mapping keys.
 */
export type ActivityPropertyName = keyof typeof PROPERTY_TO_MODEL_FIELD;

/**
 * Internal DTO matching the back-end response structure.
 * @internal
 */
interface ActivityPropertySettingDto {
  id: string;
  propertyName: ActivityPropertyName;
  showInMobile: boolean;
  showInPortal: boolean;
  readOnlyIfPortalCreated: boolean;
  organizationalUnitId: string;
}

/**
 * UI-oriented interface for activity property availability settings.
 */
export interface ActivityPropertySetting {
  id: string;
  propertyName: NestedKeyOf<ActivityModel>;
  mobileAvailability: PropertyAvailability;
  portalAvailability: PropertyAvailability;
}

/**
 * Map of ActivityModel field names to their availability configuration.
 * Used to determine which properties are visible/editable in an app.
 */
export type AppActivityPropertiesAvailability = Map<
  NestedKeyOf<ActivityModel>,
  PropertyAvailability
>;

@Injectable({
  providedIn: 'root',
})
export class ActivityPropertySettingsGateway {
  protected readonly config = inject<ConfigOptions>(ConfigService);
  protected readonly http = inject(HttpClient);

  protected readonly path = 'activities/property-settings';

  /**
   * Get activity property settings from the backend.
   * Automatically filters out properties not implemented in ActivityModel.
   * Injects frontend-only properties with default values.
   * Merges nested properties (e.g., plannedTimes.start, plannedTimes.end) into grouped properties.
   *
   * @returns Observable of settings array with both mobile and portal availability
   */
  getSettings(): Observable<ActivityPropertySetting[]> {
    return this.http
      .get<ActivityPropertySettingDto[]>(`${this.config.host}/${this.path}`)
      .pipe(
        map((dtos) => {
          const allSettings = dtos
            .map((dto) => ActivityPropertySettingsGateway.dtoToSetting(dto))
            .filter(
              (setting): setting is ActivityPropertySetting => setting !== null,
            );

          // Separate nested and regular settings
          const nestedPropertyNames = new Set<NestedKeyOf<ActivityModel>>(
            Object.values(GROUPED_TO_NESTED_PROPERTIES).flat(),
          );
          const nestedSettings = allSettings.filter((s) =>
            nestedPropertyNames.has(s.propertyName),
          );
          const regularSettings = allSettings.filter(
            (s) => !nestedPropertyNames.has(s.propertyName),
          );

          // Merge nested settings into grouped settings
          const groupedSettings: ActivityPropertySetting[] = [];
          for (const [groupedProp, nestedProps] of Object.entries(
            GROUPED_TO_NESTED_PROPERTIES,
          )) {
            const relevantSettings = nestedSettings.filter((s) =>
              (nestedProps as readonly NestedKeyOf<ActivityModel>[]).includes(
                s.propertyName,
              ),
            );
            const merged = mergeNestedSettings(relevantSettings, groupedProp);
            if (merged) {
              groupedSettings.push(merged);
            }
          }

          // Inject frontend-only properties that aren't returned by the API
          const frontendOnlySettings: ActivityPropertySetting[] =
            FRONTEND_ONLY_PROPERTIES.map((prop) => ({
              id: `frontend-only-${prop.propertyName}`,
              propertyName: prop.propertyName,
              portalAvailability: prop.portalAvailability,
              mobileAvailability: prop.mobileAvailability,
            }));

          // Filter out any frontend-only properties that the API might have started returning
          const apiPropertyNames = new Set([
            ...regularSettings.map((s) => s.propertyName),
            ...groupedSettings.map((s) => s.propertyName),
          ]);
          const frontendOnlyToAdd = frontendOnlySettings.filter(
            (s) => !apiPropertyNames.has(s.propertyName),
          );

          return [...regularSettings, ...groupedSettings, ...frontendOnlyToAdd];
        }),
      );
  }

  /**
   * Update activity property settings.
   * Automatically filters out frontend-only properties before sending to API.
   * Splits grouped properties (e.g., plannedTimes) into nested properties for the API.
   *
   * @param settings - Array of settings to update (can be partial updates)
   * @returns Observable of updated settings array (merged back to grouped properties)
   */
  updateSettings(
    settings: ActivityPropertySetting[],
  ): Observable<ActivityPropertySetting[]> {
    // Filter out frontend-only properties (not supported by API yet)
    const frontendOnlyIds = new Set(
      FRONTEND_ONLY_PROPERTIES.map((p) => `frontend-only-${p.propertyName}`),
    );
    const frontendOnlySettings = settings.filter((s) =>
      frontendOnlyIds.has(s.id),
    );

    // Separate grouped and regular settings
    const groupedPropertyNames = new Set(
      Object.keys(GROUPED_TO_NESTED_PROPERTIES),
    );
    const groupedSettings = settings.filter(
      (s) =>
        groupedPropertyNames.has(s.propertyName) && !frontendOnlyIds.has(s.id),
    );
    const regularSettings = settings.filter(
      (s) =>
        !groupedPropertyNames.has(s.propertyName) && !frontendOnlyIds.has(s.id),
    );

    // Get original settings to preserve IDs when splitting
    return this.http
      .get<ActivityPropertySettingDto[]>(`${this.config.host}/${this.path}`)
      .pipe(
        mergeMap((dtos) => {
          const originalSettings = dtos
            .map((dto) => ActivityPropertySettingsGateway.dtoToSetting(dto))
            .filter(
              (setting): setting is ActivityPropertySetting => setting !== null,
            );

          // Split grouped settings into nested settings
          const nestedSettings: ActivityPropertySetting[] = [];
          for (const groupedSetting of groupedSettings) {
            const nestedProps =
              GROUPED_TO_NESTED_PROPERTIES[groupedSetting.propertyName];
            if (nestedProps) {
              nestedSettings.push(
                ...splitGroupedSetting(
                  groupedSetting,
                  nestedProps,
                  originalSettings,
                ),
              );
            }
          }

          const apiSettings = [...regularSettings, ...nestedSettings];

          // If only frontend-only settings, return them as-is without API call
          if (apiSettings.length === 0) {
            return new Observable<ActivityPropertySetting[]>((observer) => {
              observer.next(frontendOnlySettings);
              observer.complete();
            });
          }

          const payload = {
            items: apiSettings.map((setting) =>
              ActivityPropertySettingsGateway.settingToUpdateDto(setting),
            ),
          };

          return this.http
            .patch<
              ActivityPropertySettingDto[]
            >(`${this.config.host}/${this.path}`, payload)
            .pipe(
              map((dtos) => {
                const updatedNested = dtos
                  .map((dto) =>
                    ActivityPropertySettingsGateway.dtoToSetting(dto),
                  )
                  .filter(
                    (setting): setting is ActivityPropertySetting =>
                      setting !== null,
                  );

                // Separate updated nested and regular settings
                const nestedPropertyNames = new Set<NestedKeyOf<ActivityModel>>(
                  Object.values(GROUPED_TO_NESTED_PROPERTIES).flat(),
                );
                const updatedNestedSettings = updatedNested.filter((s) =>
                  nestedPropertyNames.has(s.propertyName),
                );
                const updatedRegularSettings = updatedNested.filter(
                  (s) => !nestedPropertyNames.has(s.propertyName),
                );

                // Merge nested settings back into grouped settings
                const updatedGroupedSettings: ActivityPropertySetting[] = [];
                for (const [groupedProp, nestedProps] of Object.entries(
                  GROUPED_TO_NESTED_PROPERTIES,
                )) {
                  const relevantSettings = updatedNestedSettings.filter((s) =>
                    (
                      nestedProps as readonly NestedKeyOf<ActivityModel>[]
                    ).includes(s.propertyName),
                  );
                  const merged = mergeNestedSettings(
                    relevantSettings,
                    groupedProp,
                  );
                  if (merged) {
                    updatedGroupedSettings.push(merged);
                  }
                }

                // Merge API response with frontend-only settings
                return [
                  ...updatedRegularSettings,
                  ...updatedGroupedSettings,
                  ...frontendOnlySettings,
                ];
              }),
            );
        }),
      );
  }

  /**
   * Get availability map for a specific app using ActivityModel field names.
   *
   * This is a convenience method that filters settings by app and returns a Map.
   * Since settings already use model field names, this is a simple transformation.
   *
   * @remarks
   * This is a bridge method that filters settings client-side. This should ideally
   * be handled server-side, but the API lacks app-specific filtering endpoints.
   *
   * @param app - Target app ('mobile' or 'portal') to filter settings for
   * @returns Observable of Map with model field paths as keys (e.g., 'clientId', 'interval.start')
   *
   * @example
   * ```typescript
   * const availability = toSignal(this.gateway.getModelFieldAvailability('portal'));
   * const showClient = computed(() => availability()?.get('clientId') !== 'notAvailable');
   * const showIntervalStart = computed(() => availability()?.get('interval.start') !== 'notAvailable');
   * ```
   */
  getModelFieldAvailability(
    app: 'mobile' | 'portal',
  ): Observable<AppActivityPropertiesAvailability> {
    return this.getSettings().pipe(
      map((settings) => {
        const fieldMap = new Map<
          NestedKeyOf<ActivityModel>,
          PropertyAvailability
        >();

        for (const setting of settings) {
          const availability =
            app === 'portal'
              ? setting.portalAvailability
              : setting.mobileAvailability;
          fieldMap.set(setting.propertyName, availability);
        }

        return fieldMap;
      }),
    );
  }

  // ============================================================================
  // STATIC MAPPER METHODS
  // ============================================================================

  /**
   * Converts backend DTO to clean UI setting interface.
   * Transforms backend property names to ActivityModel field names.
   * Returns null for properties not implemented in ActivityModel (filtered out).
   *
   * @param dto - Backend DTO
   * @returns UI setting or null if property not mapped
   */
  static dtoToSetting(
    dto: ActivityPropertySettingDto,
  ): ActivityPropertySetting | null {
    const modelField = PROPERTY_TO_MODEL_FIELD[dto.propertyName];

    // Filter out properties not yet implemented in ActivityModel
    if (!modelField) {
      return null;
    }

    return {
      id: dto.id,
      propertyName: modelField,
      mobileAvailability:
        ActivityPropertySettingsGateway.mapToMobileAvailability(
          dto.showInMobile,
          dto.readOnlyIfPortalCreated,
        ),
      portalAvailability:
        ActivityPropertySettingsGateway.mapToPortalAvailability(
          dto.showInPortal,
        ),
    };
  }

  /**
   * Converts UI setting to backend update DTO.
   * Transforms ActivityModel field names back to backend property names.
   *
   * @param setting - UI setting
   * @returns Backend update DTO
   */
  static settingToUpdateDto(
    setting: ActivityPropertySetting,
  ): Omit<ActivityPropertySettingDto, 'organizationalUnitId'> {
    const mobile = ActivityPropertySettingsGateway.mapFromMobileAvailability(
      setting.mobileAvailability,
    );
    const backendPropertyName = MODEL_FIELD_TO_PROPERTY[setting.propertyName];

    return {
      id: setting.id,
      propertyName: backendPropertyName,
      showInPortal: ActivityPropertySettingsGateway.mapFromPortalAvailability(
        setting.portalAvailability,
      ),
      showInMobile: mobile.showInMobile,
      readOnlyIfPortalCreated: mobile.readOnlyIfPortalCreated,
    };
  }

  /**
   * Maps portal backend field (showInPortal) to PropertyAvailability.
   *
   * @param showInPortal - Boolean from backend
   * @returns PropertyAvailability configuration
   */
  static mapToPortalAvailability(showInPortal: boolean): PropertyAvailability {
    return showInPortal ? 'editable' : 'notAvailable';
  }

  /**
   * Maps PropertyAvailability to portal backend field (showInPortal).
   *
   * @param value - PropertyAvailability configuration
   * @returns Boolean for backend
   */
  static mapFromPortalAvailability(value: PropertyAvailability): boolean {
    return value === 'editable';
  }

  /**
   * Maps mobile backend fields (showInMobile, readOnlyIfPortalCreated) to PropertyAvailability.
   *
   * @param showInMobile - Boolean from backend
   * @param readOnlyIfPortalCreated - Boolean from backend
   * @returns PropertyAvailability configuration
   */
  static mapToMobileAvailability(
    showInMobile: boolean,
    readOnlyIfPortalCreated: boolean,
  ): PropertyAvailability {
    if (!showInMobile) return 'notAvailable';
    return readOnlyIfPortalCreated ? 'readOnly' : 'editable';
  }

  /**
   * Maps PropertyAvailability to mobile backend fields (showInMobile, readOnlyIfPortalCreated).
   *
   * @param value - PropertyAvailability configuration
   * @returns Object with backend fields
   */
  static mapFromMobileAvailability(value: PropertyAvailability): {
    showInMobile: boolean;
    readOnlyIfPortalCreated: boolean;
  } {
    switch (value) {
      case 'readOnly':
        return { showInMobile: true, readOnlyIfPortalCreated: true };
      case 'editable':
        return { showInMobile: true, readOnlyIfPortalCreated: false };
      case 'notAvailable':
        return { showInMobile: false, readOnlyIfPortalCreated: false };
    }
  }
}
